/**
 * The dashboard's window picker.
 *
 * Presets plus a custom pair, with the selection held in the URL by the route above
 * so a filtered dashboard is a link somebody can send.
 */

import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";

import {
  type DateRange,
  isValidRange,
  MAX_RANGE_DAYS,
  RANGE_PRESETS,
  type RangePreset,
} from "~/lib/analytics-range";

export interface DateRangeControlProps {
  preset: RangePreset;
  range: DateRange;
  onPresetChange: (preset: RangePreset) => void;
  onRangeChange: (range: DateRange) => void;
}

export function DateRangeControl({
  preset,
  range,
  onPresetChange,
  onRangeChange,
}: DateRangeControlProps) {
  const { t } = useTranslation();
  const invalid = preset === "custom" && !isValidRange(range);

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      sx={{ alignItems: { md: "flex-start" } }}
    >
      <Tabs
        value={preset}
        onChange={(_event, next: RangePreset) => onPresetChange(next)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={t("analytics.range.label")}
      >
        {RANGE_PRESETS.map((option) => (
          <Tab
            key={option}
            value={option}
            label={t(`analytics.range.${option}`)}
            data-testid={`analytics.range-${option}`}
          />
        ))}
      </Tabs>

      {preset === "custom" ? (
        <Stack direction="row" spacing={1.5}>
          <TextField
            type="date"
            size="small"
            label={t("analytics.range.from")}
            value={range.from}
            onChange={(event) =>
              onRangeChange({ ...range, from: event.target.value })
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "analytics.range-from" },
            }}
          />
          <TextField
            type="date"
            size="small"
            label={t("analytics.range.to")}
            value={range.to}
            onChange={(event) =>
              onRangeChange({ ...range, to: event.target.value })
            }
            error={invalid}
            helperText={
              invalid
                ? t("analytics.range.invalid", { days: MAX_RANGE_DAYS })
                : undefined
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "analytics.range-to" },
            }}
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
